/**
 * Portal Stage Engine — single source of truth for portal timeline state.
 *
 * Every surface that renders or gates on portal stages (staff workspace stepper,
 * sidebar project chips, advance/retreat buttons, and the client-facing splash)
 * must consume this module instead of computing status locally. Historically
 * there were three divergent computations which caused the timeline to disagree
 * with itself — especially after a manual retreat, when external "bumps"
 * (released model, sent/approved proposal, won deal, paid design fee) silently
 * re-advanced the rendered stage.
 *
 * Retreat semantics: a manual retreat writes `stageManualOverride: true` on the
 * portal project. While the override is set, the stored stage renders exactly
 * as stored — no auto-bumps. Any subsequent advance clears the override and
 * re-enables bumps.
 */

import type { ClientPortal, PortalProject, PortalStage } from '../types';
import { portalHasModelContent } from './portalStageProgress';

export const PORTAL_STAGE_ORDER: PortalStage[] = [
  'design-fee',
  '3d-model',
  'quote',
  'quote-signed',
  '2d-plans',
  'in-production',
  'delivery',
];

export type PortalStepStatus = 'completed' | 'active' | 'locked' | 'skipped';

export interface PortalStageSignals {
  /** Raw Firestore stage (per-project row, or legacy flat field). */
  storedStage: PortalStage;
  /** True when a manual retreat pinned the stage — suppresses all auto-bumps. */
  manualOverride?: boolean;
  designFeePaid?: boolean;
  designFeeSkipped?: boolean;
  modelStepSkipped?: boolean;
  /** Released or uploaded 3D content exists for this project. */
  hasModelContent?: boolean;
  /** Proposal builder status for this deal ('draft' | 'sent' | 'opened' | 'approved' | ...). */
  proposalStatus?: string | null;
  dealOutcome?: 'won' | 'lost' | 'active';
}

export function portalStageIndex(stage: PortalStage): number {
  return PORTAL_STAGE_ORDER.indexOf(stage);
}

export function nextPortalStage(stage: PortalStage): PortalStage | null {
  const idx = portalStageIndex(stage);
  if (idx < 0 || idx >= PORTAL_STAGE_ORDER.length - 1) return null;
  return PORTAL_STAGE_ORDER[idx + 1];
}

export function prevPortalStage(stage: PortalStage): PortalStage | null {
  const idx = portalStageIndex(stage);
  if (idx <= 0) return null;
  return PORTAL_STAGE_ORDER[idx - 1];
}

function maxStage(a: PortalStage, b: PortalStage): PortalStage {
  return portalStageIndex(a) >= portalStageIndex(b) ? a : b;
}

/**
 * Display stage = stored stage plus auto-bumps from external signals, unless a
 * manual override is pinned (in which case stored renders exactly as stored).
 * Quote Signed is not a display current unless the proposal is approved — a
 * staff "mark signed" does not unlock that step for the client.
 */
export function resolveDisplayStage(signals: PortalStageSignals): PortalStage {
  const stored = signals.storedStage || 'design-fee';
  if (signals.manualOverride) return stored;

  let stage = stored;
  if (signals.hasModelContent && !signals.modelStepSkipped) {
    stage = maxStage(stage, '3d-model');
  }
  if (signals.proposalStatus && ['sent', 'opened', 'approved'].includes(signals.proposalStatus)) {
    stage = maxStage(stage, 'quote');
  }
  if (signals.proposalStatus === 'approved') {
    stage = maxStage(stage, 'quote-signed');
  } else if (stage === 'quote-signed') {
    // Staff can pin the stored stage at quote-signed before the client signs.
    // Do not show Quote Signed as current until the proposal is approved.
    stage = 'quote';
  }
  return stage;
}

/**
 * Per-step status for the full 7-stage timeline.
 *
 * Notes:
 * - "active" on a step *behind* the current stage means incomplete prior work
 *   (unpaid design fee, missing 3D content) — the timeline flags it instead of
 *   showing false green.
 * - Under manual override no bump applies, including the paid-fee bump, so a
 *   retreated stage always renders as the actual stage.
 */
export function computePortalStepStatuses(
  signals: PortalStageSignals,
): Record<PortalStage, PortalStepStatus> {
  let displayStage = resolveDisplayStage(signals);
  if (signals.proposalStatus !== 'approved' && displayStage === 'quote-signed') {
    displayStage = 'quote';
  }
  let currentIndex = portalStageIndex(displayStage);

  // Paid/skipped design fee implies progress into 3D even when stage lags —
  // but never under a manual override.
  if (
    !signals.manualOverride &&
    displayStage === 'design-fee' &&
    (signals.designFeePaid || signals.designFeeSkipped)
  ) {
    currentIndex = portalStageIndex('3d-model');
  }

  const statuses = {} as Record<PortalStage, PortalStepStatus>;

  for (const stage of PORTAL_STAGE_ORDER) {
    const stepIndex = portalStageIndex(stage);

    if (stage === 'design-fee' && signals.designFeeSkipped) {
      statuses[stage] = 'skipped';
      continue;
    }
    if (stage === '3d-model' && signals.modelStepSkipped) {
      statuses[stage] = 'skipped';
      continue;
    }
    if (stage === 'quote-signed' && signals.proposalStatus !== 'approved') {
      statuses[stage] = 'locked';
      continue;
    }

    if (stepIndex < currentIndex) {
      if (stage === 'design-fee' && !signals.designFeePaid) {
        statuses[stage] = 'active'; // incomplete prior step
        continue;
      }
      if (stage === '3d-model' && !signals.hasModelContent) {
        statuses[stage] = 'active'; // incomplete prior step
        continue;
      }
      statuses[stage] = 'completed';
      continue;
    }

    if (stepIndex === currentIndex) {
      statuses[stage] = 'active';
      continue;
    }

    // Ahead of current: locked, except released 3D content unlocks that step
    // for viewing (no bump under manual override).
    if (stage === '3d-model' && signals.hasModelContent && !signals.manualOverride) {
      statuses[stage] = 'active';
      continue;
    }
    statuses[stage] = 'locked';
  }

  return statuses;
}

/**
 * Build engine signals from a merged per-project portal view
 * (output of `buildEffectivePortalProject`) plus CRM context.
 */
export function signalsFromPortalProject(
  project: Pick<
    ClientPortal | PortalProject,
    | 'currentStage'
    | 'designFeePaid'
    | 'designFeeSkipped'
    | 'modelStepSkipped'
    | 'modelUrl'
    | 'modelImageUrl'
    | 'modelLoomUrl'
  > & { stageManualOverride?: boolean },
  opts?: {
    releasedModelUrl?: string;
    proposalStatus?: string | null;
    dealOutcome?: 'won' | 'lost' | 'active';
  },
): PortalStageSignals {
  const hasModel =
    portalHasModelContent(project) ||
    (!!opts?.releasedModelUrl?.trim() && !project.modelStepSkipped);
  return {
    storedStage: (project.currentStage as PortalStage) || 'design-fee',
    manualOverride: !!project.stageManualOverride,
    designFeePaid: !!project.designFeePaid,
    designFeeSkipped: !!project.designFeeSkipped,
    modelStepSkipped: !!project.modelStepSkipped,
    hasModelContent: hasModel,
    proposalStatus: opts?.proposalStatus,
    dealOutcome: opts?.dealOutcome,
  };
}

/** Short human label for a stage (chips, sidebar, activity). */
export const PORTAL_STAGE_SHORT_LABELS: Record<PortalStage, string> = {
  'design-fee': 'Fee',
  '3d-model': 'Model',
  quote: 'Quote',
  'quote-signed': 'Signed',
  '2d-plans': 'Plans',
  'in-production': 'Prod',
  delivery: 'Delivery',
};

export const PORTAL_STAGE_FULL_LABELS: Record<PortalStage, string> = {
  'design-fee': 'Concept Design Fee',
  '3d-model': '3D Model',
  quote: 'Your Quote',
  'quote-signed': 'Quote Signed',
  '2d-plans': '2D Plans',
  'in-production': 'In Production',
  delivery: 'Delivery & Install',
};
